/* eslint-disable valid-jsdoc */
import dayjs from 'dayjs';
import {
  fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull,
} from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import {
  cloneData, cloneDataOrNull, DataModel, DataModelBuilder, isSameData,
  SerializationContext, SerializedData,
} from '../dto/dto.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { CustomerProfile } from './customer-profile.js';
import { DateSpan } from './date-span.js';
import { TicketTemplate } from './ticket-template';
import { TimeZoneRegion, getCountryOfTimeZone } from './time-zone-region.js';
import { VersionRange } from '../core/version-range.js';
import { TicketType } from './ticket-type.js';
import { assertUnreachable } from '../../utility/general-utility.js';
import { Country } from './country.js';
import { isNotNullOrUndefined } from '../../utility/null-safety.js';
import { IllegalOperationException } from '../../exceptions/illegal-operation-exception.js';
import { SeatingOptions } from './seating-options.js';
import { Discount } from './discounts/discount.js';
import { ElectronicDeliveryMethod } from './electronic-delivery-method.js';
import { TrueTime } from '../../time/true-time.js';
import { CustomerAccountBase } from './customer-account-base.js';
import { Fee } from './fee.js';
import { toSafeFilename } from '../../utility/string.js';
import { SearchUtils } from '../../utility/search-utils.js';
import { IProductWithSerialNumber } from '../../contracts/product-with-serial-number.js';
import { TicketSupplement } from './ticket-supplement.js';
import { Language } from './language.js';

export enum EventTicketStatus {
  Live = 'LIVE',
  Aborted = 'ABORTED',
  Canceled = 'CANCELED',
  Refunded = 'REFUNDED',
  Scanned = 'SCANNED',
}

export class EventTicket extends DataModel implements IProductWithSerialNumber {
  static readonly TYPE_ID: string = 'bee3995077e24a1e81f9ca341c3995fe';
  static readonly SCHEMA_VERSION: number = 18;

  public readonly templateId: string;

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly cancelledDate: dayjs.Dayjs | null,
    public readonly cancelledBy: string | null,
    public readonly country: Country,
    public readonly timezone: TimeZoneRegion,
    public readonly businessRef: BusinessModuleRef,
    public readonly status: EventTicketStatus,
    public readonly eventId: string,
    public readonly orderId: string,
    public readonly orderEntryId: string | null,
    public readonly promoterId: string | null,
    public readonly dateSpan: DateSpan,
    public readonly template: TicketTemplate,
    public readonly ticketType: TicketType,
    public readonly deliveryMethod: ElectronicDeliveryMethod,
    public readonly storagePath: string | null,
    public readonly seatingOptions: SeatingOptions | null,
    public readonly scanDate: dayjs.Dayjs | null,
    public readonly scannerId: string | null,
    public readonly applicationFee: Fee | null, // Fee taken by In Time on top of the ticket price.
    public readonly discount: Discount | null,
    public readonly serialNumber: string | null,
    public readonly supplement: TicketSupplement | null,
    public readonly language: Language | null,
    public readonly customerId: string | null,
    public readonly customerName: string | null,
    public readonly customerEmail: string | null,
    public readonly userId: string | null,
  ) {
    super();
    this.templateId = template.uniqueId;
  }

  get $typeId(): string {
    return EventTicket.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(16, EventTicket.SCHEMA_VERSION);
  }

  static create(params: {
    uniqueId: string,
    creatorId: string,
    creationDate: dayjs.Dayjs,
    cancelledDate: dayjs.Dayjs | null,
    cancelledBy: string | null,
    country: Country,
    timezone: TimeZoneRegion,
    businessRef: BusinessModuleRef,
    status: EventTicketStatus,
    eventId: string,
    orderId: string,
    orderEntryId: string | null,
    promoterId: string | null,
    dateSpan: DateSpan,
    template: TicketTemplate,
    ticketType: TicketType,
    deliveryMethod: ElectronicDeliveryMethod,
    storagePath: string | null,
    seatingOptions: SeatingOptions | null,
    scanDate: dayjs.Dayjs | null,
    scannerId: string | null,
    applicationFee: Fee | null, // Fee taken by In Time on top of the ticket price.
    discount: Discount | null,
    serialNumber: string | null,
    supplement: TicketSupplement | null,
    language: Language | null,
    customerId: string | null,
    customerName: string | null,
    customerEmail: string | null,
    userId: string | null,
  }): EventTicket {
    return new EventTicket(
        params.uniqueId,
        params.creatorId,
        params.creationDate,
        params.cancelledDate,
        params.cancelledBy,
        params.country,
        params.timezone,
        params.businessRef,
        params.status,
        params.eventId,
        params.orderId,
        params.orderEntryId,
        params.promoterId,
        params.dateSpan,
        params.template,
        params.ticketType,
        params.deliveryMethod,
        params.storagePath,
        params.seatingOptions,
        params.scanDate,
        params.scannerId,
        params.applicationFee,
        params.discount,
        params.serialNumber,
        params.supplement,
        params.language,
        params.customerId,
        params.customerName,
        params.customerEmail,
        params.userId,
    );
  }

  static basic(params: {
    uniqueId: string,
    creatorId: string,
    creationDate: dayjs.Dayjs,
    country: Country,
    timezone: TimeZoneRegion,
    businessRef: BusinessModuleRef,
    status: EventTicketStatus,
    eventId: string,
    orderId: string,
    orderEntryId: string | null,
    promoterId: string | null,
    dateSpan: DateSpan,
    template: TicketTemplate,
    ticketType: TicketType,
    deliveryMethod: ElectronicDeliveryMethod,
    storagePath: string | null,
    applicationFee: Fee | null,
    discount: Discount | null,
    seatingOptions: SeatingOptions | null,
    language: Language | null,
    customerId: string | null,
    customerName: string | null,
    customerEmail: string | null,
    userId: string | null,
  }): EventTicket {
    return this.create({
      ...params,
      cancelledDate: null,
      cancelledBy: null,
      scanDate: null,
      scannerId: null,
      serialNumber: null,
      supplement: null,
    });
  }

  static fromMap(context: SerializationContext, map: SerializedData): EventTicket {
    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      creatorId: MapUtils.getString(map, 'creatorId'),
      creationDate: context.decodeDate(MapUtils.getNumber(map, 'creationDate')),
      cancelledDate: context.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'cancelledDate')),
      cancelledBy: MapUtils.getStringOrNull(map, 'cancelledBy'),
      country: toEnumFromString(MapUtils.getString(map, 'country'), Country),
      timezone: toEnumFromString(MapUtils.getString(map, 'timezone'), TimeZoneRegion),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      status: toEnumFromString(MapUtils.getString(map, '$status'), EventTicketStatus),
      eventId: MapUtils.getString(map, 'eventId'),
      orderId: MapUtils.getString(map, 'orderId'),
      orderEntryId: MapUtils.getStringOrNull(map, 'orderEntryId'),
      promoterId: MapUtils.getStringOrNull(map, 'promoterId'),
      dateSpan: context.fromData<DateSpan>(MapUtils.getRecord(map, 'dateSpan')),
      template: context.fromData<TicketTemplate>(MapUtils.getRecord(map, 'template')),
      ticketType: toEnumFromString(MapUtils.getString(map, 'ticketType'), TicketType),
      deliveryMethod: toEnumFromString(MapUtils.getString(map, 'deliveryMethod'), ElectronicDeliveryMethod),
      storagePath: MapUtils.getStringOrNull(map, 'storagePath'),
      seatingOptions: context.fromDataOrNull<SeatingOptions>(MapUtils.getRecordOrNull(map, 'seatingOptions')),
      scanDate: context.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'scanDate')),
      scannerId: MapUtils.getStringOrNull(map, 'scannerId'),
      applicationFee: context.fromDataOrNull<Fee>(MapUtils.getRecordOrNull(map, 'applicationFee')),
      discount: context.fromDataOrNull<Discount>(MapUtils.getRecordOrNull(map, 'discount')),
      serialNumber: MapUtils.getStringOrNull(map, 'serialNumber'),
      supplement: context.fromDataOrNull<TicketSupplement>(MapUtils.getRecordOrNull(map, 'supplement')),
      language: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'language'), Language),
      customerId: MapUtils.getStringOrNull(map, 'customerId'),
      customerName: MapUtils.getStringOrNull(map, 'customerName'),
      customerEmail: MapUtils.getStringOrNull(map, 'customerEmail'),
      userId: MapUtils.getStringOrNull(map, 'userId'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'creationDate': context.encodeDate(this.creationDate),
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
      'country': fromEnumAsString(this.country),
      'timezone': fromEnumAsString(this.timezone),
      'businessRef': this.businessRef.encode(),
      '$status': fromEnumAsString(this.status),
      'eventId': this.eventId,
      'orderId': this.orderId,
      'orderEntryId': this.orderEntryId,
      'promoterId': this.promoterId,
      'serialNumber': this.serialNumber,
      'dateSpan': context.toData(this.dateSpan),
      'template': context.toData(this.template),
      'templateId': this.template.uniqueId,
      'ticketType': fromEnumAsString<TicketType>(this.ticketType),
      'deliveryMethod': fromEnumAsString<ElectronicDeliveryMethod>(this.deliveryMethod),
      'storagePath': this.storagePath,
      'applicationFee': context.toDataOrNull(this.applicationFee),
      'discount': context.toDataOrNull(this.discount),
      'scanDate': context.encodeDateOrNull(this.scanDate),
      'scannerId': this.scannerId ?? null,
      'seatingOptions': context.toDataOrNull(this.seatingOptions),
      'supplement': context.toDataOrNull(this.supplement),
      'language': fromEnumAsStringOrNull(this.language),
      'customerId': this.customerId,
      'customerName': this.customerName,
      'customerEmail': this.customerEmail,
      'userId': this.userId,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  isSame(other: EventTicket): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.creatorId === this.creatorId &&
      other.creationDate === this.creationDate &&
      other.cancelledDate === this.cancelledDate &&
      other.cancelledBy === this.cancelledBy &&
      other.country === this.country &&
      other.timezone === this.timezone &&
      other.businessRef === this.businessRef &&
      other.status === this.status &&
      other.eventId === this.eventId &&
      other.orderId === this.orderId &&
      other.orderEntryId === this.orderEntryId &&
      other.userId === this.userId &&
      other.promoterId === this.promoterId &&
      other.serialNumber === this.serialNumber &&
      isSameData(other.dateSpan, this.dateSpan) &&
      isSameData(other.seatingOptions, this.seatingOptions) &&
      isSameData(other.template, this.template) &&
      isSameData(other.applicationFee, this.applicationFee) &&
      isSameData(other.discount, this.discount) &&
      other.ticketType === this.ticketType &&
      other.deliveryMethod === this.deliveryMethod &&
      other.storagePath === this.storagePath &&
      other.scanDate === this.scanDate &&
      other.scannerId === this.scannerId &&
      other.serialNumber === this.serialNumber &&
      other.language === this.language &&
      other.customerId === this.customerId &&
      other.customerName === this.customerName &&
      other.customerEmail === this.customerEmail &&
      isSameData(other.supplement, this.supplement);
  }

  convertToEmailDelivery(storagePath: string): this {
    return this.copyWith({
      deliveryMethod: ElectronicDeliveryMethod.Email,
      storagePath: storagePath,
    });
  }

  assignToCustomerAccount(account: CustomerAccountBase): this {
    return this.copyWith({
      creatorId: account.uniqueId,
      userId: account.uniqueId,
    }) as this;
  }

  markAsScanned(scannerId: string): this {
    return this.copyWith({
      scanDate: TrueTime.now(),
      scannerId: scannerId,
      status: EventTicketStatus.Scanned,
    });
  }

  markAsRestored(): this {
    return this.copyWith({
      status: EventTicketStatus.Live,
      scanDate: null,
      scannerId: null,
    });
  }

  copyAsAborted(): this {
    return this.copyWith({
      status: EventTicketStatus.Aborted,
      supplement: this.supplement?.copyAsAborted(),
    });
  }

  copyAsCanceled(
      cancelledBy: string | null,
      cancelledDate: dayjs.Dayjs,
      isRefund: boolean,
      options: { shouldCancelSupplement: boolean },
  ): this {
    return this.copyWith({
      cancelledDate: this.cancelledDate ?? cancelledDate,
      cancelledBy: this.cancelledBy ?? cancelledBy,
      status: isRefund ? EventTicketStatus.Refunded : EventTicketStatus.Canceled,
      supplement: options.shouldCancelSupplement ?
        this.supplement?.maybeCopyAsCanceled(cancelledBy, cancelledDate, isRefund): undefined,
    });
  }

  copyWithSupplementAsCanceled(
      cancelledBy: string | null,
      cancelledDate: dayjs.Dayjs,
      isRefund: boolean,
  ): this {
    return this.copyWith({
      supplement: this.supplement?.maybeCopyAsCanceled(cancelledBy, cancelledDate, isRefund),
    });
  }

  copyWithSeatingOptions(seatingOptions: SeatingOptions): this {
    return this.copyWith({
      seatingOptions: seatingOptions.clone(),
    });
  }

  copyWithScannedSupplement({
    scanDate,
    scannerId = null,
  }: {
    scanDate: dayjs.Dayjs,
    scannerId?: string | null,
  }): this {
    return this.copyWith({
      supplement: this.supplement?.copyAsScanned({
        scanDate,
        scannerId,
      }),
    });
  }

  copyWith({
    uniqueId = this.uniqueId,
    creatorId = this.creatorId,
    creationDate = TimeZone.cloneDate(this.creationDate),
    cancelledDate = TimeZone.cloneDateOrNull(this.cancelledDate),
    cancelledBy = this.cancelledBy,
    country = this.country,
    timezone = this.timezone,
    businessRef = cloneData(this.businessRef),
    status = this.status,
    eventId = this.eventId,
    orderId = this.orderId,
    orderEntryId = this.orderEntryId,
    promoterId = this.promoterId,
    dateSpan = cloneData(this.dateSpan),
    template = cloneData(this.template),
    ticketType = this.ticketType,
    deliveryMethod = this.deliveryMethod,
    storagePath = this.storagePath,
    seatingOptions = cloneDataOrNull(this.seatingOptions),
    scanDate = TimeZone.cloneDateOrNull(this.scanDate),
    scannerId = this.scannerId,
    applicationFee = cloneDataOrNull(this.applicationFee),
    discount = cloneDataOrNull(this.discount),
    serialNumber = this.serialNumber,
    supplement = cloneDataOrNull(this.supplement),
    language = this.language,
    customerId = this.customerId,
    customerName = this.customerName,
    customerEmail = this.customerEmail,
    userId = this.userId,
  }: {
    uniqueId?: string,
    creatorId?: string,
    creationDate?: dayjs.Dayjs,
    cancelledDate?: dayjs.Dayjs | null,
    cancelledBy?: string | null,
    country?: Country,
    timezone?: TimeZoneRegion,
    businessRef?: BusinessModuleRef,
    status?: EventTicketStatus,
    eventId?: string,
    orderId?: string,
    orderEntryId?: string | null,
    promoterId?: string | null,
    dateSpan?: DateSpan,
    template?: TicketTemplate,
    ticketType?: TicketType,
    deliveryMethod?: ElectronicDeliveryMethod,
    storagePath?: string | null,
    seatingOptions?: SeatingOptions | null,
    scanDate?: dayjs.Dayjs | null,
    scannerId?: string | null,
    applicationFee?: Fee | null,
    discount?: Discount | null,
    serialNumber?: string | null,
    supplement?: TicketSupplement | null,
    language?: Language | null,
    customerId?: string | null,
    customerName?: string | null,
    customerEmail?: string | null,
    userId?: string | null,
  } = {}): this {
    return EventTicket.create({
      uniqueId,
      creatorId,
      creationDate,
      cancelledDate,
      cancelledBy,
      country,
      timezone,
      businessRef,
      status,
      eventId,
      orderId,
      orderEntryId,
      promoterId,
      dateSpan,
      template,
      ticketType,
      deliveryMethod,
      storagePath,
      seatingOptions,
      scanDate,
      scannerId,
      applicationFee,
      discount,
      serialNumber,
      supplement,
      language,
      customerId,
      customerName,
      customerEmail,
      userId,
    }) as this;
  }

  setSerialNumber(serialNumber: string): EventTicket {
    if(isNotNullOrUndefined(this.serialNumber)) {
      throw new IllegalOperationException(`Cannot set serial number {${serialNumber}} for ticket {${this.uniqueId}} of event {${this.eventId}} because it already has a serial number {${this.serialNumber}}`);
    }

    return this.copyWith({
      serialNumber: serialNumber,
    });
  }

  generateSearchQueryArray() : string[] {
    return SearchUtils.generateSearchQueryArray([this.uniqueId]);
  }

  static encodedStatusSafe(status: EventTicketStatus): string {
    return fromEnumAsString(status);
  }

  /**
   * Generates the storage path for a standard ticket.
   * @param {string} customerId The customer ID.
   * @param {string} orderId The order ID.
   * @param {string} ticketId The ticket ID.
   * @param {string} eventName The event name.
   * @return {string} The storage path.
   */
  static generateCustomerStoragePath({ customerId, orderId, ticketId, eventName }: {
      customerId: string,
      orderId: string,
      ticketId: string,
      eventName: string,
  }): string {
    const sanitizedEventName = toSafeFilename(eventName).substring(0, 30);
    const filename = `${sanitizedEventName}_${ticketId}`;

    return `user_accounts/${customerId}/orders/${orderId}/tickets/${filename}.pdf`;
  }

  /**
   * Generates the storage path for a protocol a ticket.
   * @param {string} orderId The order ID.
   * @param {string} ticketId The ticket ID.
   * @param {string} businessId The business ID.
   * @param {string} eventName The event name.
   * @return {string} The storage path.
   */
  static generateGuestStoragePath({ orderId, ticketId, businessId, eventName }: {
      orderId: string,
      ticketId: string,
      businessId: string,
      eventName: string,
  }): string {
    const sanitizedEventName = toSafeFilename(eventName).substring(0, 30);
    const filename = `${sanitizedEventName}_${ticketId}`;

    return `business_profiles/${businessId}/orders/${orderId}/tickets/${filename}.pdf`;
  }

  /**
   * Generates the storage path for a protocol a ticket.
   * @param {string} orderId The order ID.
   * @param {string} ticketId The ticket ID.
   * @param {string} businessId The business ID.
   * @param {string} eventName The event name.
   * @return {string} The storage path.
   */
  static generateAdminStoragePath({ orderId, ticketId, businessId, eventName }: {
      orderId: string,
      ticketId: string,
      businessId: string,
      eventName: string,
  }): string {
    const sanitizedEventName = toSafeFilename(eventName).substring(0, 30);
    const filename = `${sanitizedEventName}_${ticketId}`;

    return `business_profiles/${businessId}/orders/${orderId}/tickets/${filename}.pdf`;
  }
}

export class EventTicketBuilder extends DataModelBuilder<EventTicket> {
  build(context: SerializationContext, map: SerializedData): EventTicket {
    return EventTicket.fromMap(context, map);
  }

  generateStandardLegacyStoragePath(
      customerId: string,
      orderId: string,
      ticketId: string
  ): string {
    const filename = `ticket-${ticketId}`;

    return `user_accounts/${customerId}/orders/${orderId}/tickets/${filename}.pdf`;
  }

  generateProtocolLegacyStoragePath(
      orderId: string,
      ticketId: string,
      businessId: string
  ): string {
    const filename = `ticket-${ticketId}`;

    return `business_profiles/${businessId}/orders/${orderId}/tickets/${filename}.pdf`;
  }

  optimizeForQuery(context: SerializationContext, model: EventTicket): SerializedData | null {
    if(context.storage.get<boolean>('avoidOptimizeForQuery') === true) {
      return null;
    }

    return {
      'search': model.generateSearchQueryArray(),
    };
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, 'ticketType', TicketType, TicketType.Standard);
    this.migrateEnum(map, 'deliveryMethod', ElectronicDeliveryMethod, ElectronicDeliveryMethod.Email);

    MapUtils.replace(
        map, '$status', 'DELETED',
        fromEnumAsString(EventTicketStatus.Canceled),
    );

    if(versionRange.contains(EventTicket.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(EventTicket.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the [Active] status was renamed to [Live]
    if(version < 2) {
      const status = MapUtils.getString(map, '$status');
      if(status === 'ACTIVE') {
        MapUtils.set(map, '$status', fromEnumAsString(EventTicketStatus.Live));
      }

      version = 2;
    }

    // In this version parameter `ticketType` of type `TicketOrderType` was introduced.
    if(version < 3) {
      version = 3;
    }

    // In this version parameters `country` and `serialNumber` were introduced.
    if(version < 4) {
      version = 4;

      const timezone = toEnumFromString(MapUtils.getString(map, 'timezone'), TimeZoneRegion);
      const country = getCountryOfTimeZone(timezone);

      MapUtils.setIfNullOrUndefined(map, 'country', fromEnumAsString(country));
      MapUtils.setIfNullOrUndefined(map, 'serialNumber', null);
    }

    /**
     * Version 5:
     * - Add `storagePath` of type [string].
     */
    if(version < 5) {
      version = 5;
      const ticketType = toEnumFromString(MapUtils.getString(map, 'ticketType'), TicketType);

      let legacyStoragePath: string | null;
      // TODO(anyone): handle promoter ticket storage path if necessary.
      switch(ticketType) {
        case TicketType.Protocol: {
          const orderId = MapUtils.getString(map, 'orderId');
          const ticketId = MapUtils.getString(map, 'uniqueId');
          const businessRef = BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef'));
          legacyStoragePath = this.generateProtocolLegacyStoragePath(orderId, ticketId, businessRef.uniqueId);
        } break;
        case TicketType.Promoter:
          // fallthrough
        case TicketType.Standard: {
          const customerId = MapUtils.getString(map, 'customerId');
          const orderId = MapUtils.getString(map, 'orderId');
          const ticketId = MapUtils.getString(map, 'uniqueId');
          legacyStoragePath = this.generateStandardLegacyStoragePath(customerId, orderId, ticketId);
        } break;
        case TicketType.HardTicket: {
          legacyStoragePath = null;
        } break;
        default: {
          assertUnreachable(ticketType);
        }
      }

      MapUtils.setIfNullOrUndefined(map, 'storagePath', legacyStoragePath);
    }

    /**
     * Version 6:
     * - Add `promoterId` of type [string | null].
     */
    if(version < 6) {
      version = 6;
    }

    /**
     * Version 7:
     * - Add ticket type {@link TicketType.HardTicket}.
     * - Change `customerId` type from [string] to [string | null].
     * - Change `customer` type from [CustomerProfile] to [CustomerProfile | null].
     * - Change `storagePath` type from [string] to [string | null].
     */
    if(version < 7) {
      version = 7;
    }

    /**
     * Version 8:
     * - Add `seatingOptions` of type [SeatingOptions | null].
     */
    if(version < 8) {
      version = 8;
    }

    /**
     * Version 9:
     * - Add `scanDate` of type [Dayjs | null].
     */
    if(version < 9) {
      version = 9;
    }

    /**
     * Version 10:
     * - Add `scannerId` of type [string | null].
     */
    if(version < 10) {
      version = 10;
    }

    /**
     * Version 11:
     * - Add `discount` of type [Discount | null].`
     */
    if(version < 11) {
      version = 11;
    }

    /**
     * Version 12:
     * - Add `discount` of type [Discount | null].`
     */
    if(version < 12) {
      version = 12;
      const ticketType = toEnumFromString(MapUtils.getString(map, 'ticketType'), TicketType);
      map['deliveryMethod'] = fromEnumAsString(ticketType === TicketType.HardTicket ? ElectronicDeliveryMethod.Printer : ElectronicDeliveryMethod.Email);
    }

    // In this version `applicationFee` of type `Fee` was introduced.
    // Default value is `null`.
    if(version < 13) {
      map['applicationFee'] = null;
      version = 13;
    }

    // In this version the [cancelledDate] and [cancelledBy] fields were added
    if(version < 14) {
      version = 14;
    }

    /**
     * Version 15:
     * - add `supplement` of type `TicketSupplement | null`.
     */
    if(version < 15) {
      version = 15;
    }

    /**
     * Version 16:
     * - we made fields `customer` and `contactDetails` private
     * - we introduced standalone customer related fields
     */
    if(version < 16) {
      const customer = context.fromDataOrNull<CustomerProfile>(MapUtils.getRecordOrNull(map, 'customer'));

      map['language'] = fromEnumAsStringOrNull(customer?.language ?? null);
      map['customerId'] = customer?.uniqueId ?? null;
      map['customerName'] = customer?.formattedName ?? null;
      map['customerEmail'] = customer?.email ?? null;
      map['userId'] = customer?.userId ?? null;

      version = 16;
    }

    /**
     * Version 17:
     * - we removed the `customer` and `contactDetails` fields
     */
    if(version < 17) {
      version = 17;
    }

    /**
     * Version 18:
     * - add `orderEntryId` of type `string | null`.
     */
    if(version < 18) version = 18;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
